/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable @typescript-eslint/ban-ts-ignore */
/* third-party */
import React from 'react';
import get from 'lodash/get';
import { Path } from 'app/const/Path';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useUpdateEffect, useUnmount } from 'react-use';
import {
  selectedFilterAtom,
  transactionTypeAtom,
} from 'app/state/recoil/atoms';
import { useRouteMatch, useLocation } from 'react-router-dom';
import { useStoreActions, useStoreState } from 'app/state/store/hooks';
import {
  getAPIFormattedFilters,
  AF_COUNTRY,
  AF_TRANSACTION_PROVIDER_ORG_REF,
  AF_PARTICIPATING_ORG_REF,
  AF_REPORTING_ORG_REF,
} from 'app/utils/getAPIFormattedFilters';
/* project */
import { DetailModuleModel } from 'app/modules/detail-module/data';
import { DetailModuleLayout } from 'app/modules/detail-module/layout';
import { getPageTitle } from 'app/modules/detail-module/utils/getPageTitle';
import { getBreadcrumbs } from 'app/modules/detail-module/utils/getBreadcrumbs';
import { getDetailBaseFilter } from 'app/modules/detail-module/utils/getDetailBaseFilter';
import { overviewStatData } from 'app/fragments/overview-fragment/common/overviewStatData';

import { ActionCreator } from 'easy-peasy';
import { getIso2Code } from 'app/state/data/Countries';
import { OverviewStatItemProps } from 'app/fragments/overview-fragment/common/OverviewStatItem';

export default function DetailModule(props: DetailModuleModel) {
  const location = useLocation();
  const { params } = useRouteMatch();
  const pageParam = decodeURIComponent(get(params, 'param', ''));
  const [statData, setStatData] = React.useState(overviewStatData);
  const [selectedFilters, _] = useRecoilState(selectedFilterAtom);
  const transactionType = useRecoilValue(transactionTypeAtom);
  const detailBaseFilter = getDetailBaseFilter(props.type);

  const param = props.type === 'country' ? getIso2Code(pageParam) : pageParam;

  const lineInitData = {
    id: '',
    color: '',
    data: [],
  };

  const countryDetailData = useStoreState((state) => state.countryDetail.data);
  const countryDetailAction = useStoreActions(
    (actions) => actions.countryDetail.fetch
  );

  const donorDetailData = useStoreState((state) => state.donorDetail.data);
  const donorDetailAction = useStoreActions(
    (actions) => actions.donorDetail.fetch
  );

  const publisherDetailData = useStoreState(
    (state) => state.publisherDetail.data
  );
  const publisherDetailAction = useStoreActions(
    (actions) => actions.publisherDetail.fetch
  );

  const organisationDetailData = useStoreState(
    (state) => state.organisationDetail.data
  );
  const organisationDetailAction = useStoreActions(
    (actions) => actions.organisationDetail.fetch
  );

  const lineData = useStoreState((state) => ({
    count: get(state.linechart, 'data.count', 0),
    vizData: get(state.linechart, 'data.vizData', lineInitData),
  }));

  const lineDataAction = useStoreActions((actions) => actions.linechart.fetch);

  let detailData = countryDetailData;
  if (props.type === 'donor') {
    detailData = donorDetailData;
  }
  if (props.type === 'publisher') {
    detailData = publisherDetailData;
  }
  if (props.type === 'organisation') {
    detailData = organisationDetailData;
  }

  // clear state actions
  const countryDetailClearAction: ActionCreator = useStoreActions(
    (actions) => actions.countryDetail.clear
  );
  const donorDetailClearAction: ActionCreator = useStoreActions(
    (actions) => actions.donorDetail.clear
  );
  const publisherDetailClearAction: ActionCreator = useStoreActions(
    (actions) => actions.publisherDetail.clear
  );
  const organisationDetailClearAction: ActionCreator = useStoreActions(
    (actions) => actions.organisationDetail.clear
  );
  const treemapClearAction: ActionCreator = useStoreActions(
    (actions) => actions.treemap.clear
  );
  const sunburstClearAction: ActionCreator = useStoreActions(
    (actions) => actions.sunburst.clear
  );
  const lineClearAction: ActionCreator = useStoreActions(
    (actions) => actions.linechart.clear
  );
  const sectorsTableClearAction: ActionCreator = useStoreActions(
    (actions) => actions.sectorsTable.clear
  );
  const detailPublishersTableClearAction: ActionCreator = useStoreActions(
    (actions) => actions.detailPublishersTable.clear
  );
  const detailDonorsTableClearAction: ActionCreator = useStoreActions(
    (actions) => actions.detailDonorsTable.clear
  );
  const filtersUpdatedClear: ActionCreator = useStoreActions(
    (actions) => actions.filtersUpdated.clear
  );

  function loadData() {
    const filters = getAPIFormattedFilters(selectedFilters);
    lineDataAction({
      values: {
        filters: {
          ...filters,
          [detailBaseFilter]: [param],
        },
        transactionTypeCode: transactionType.code,
      },
    });
    if (props.type === 'country') {
      countryDetailAction({
        values: {
          filters: {
            ...filters,
            // @ts-ignore
            [AF_COUNTRY]: [param],
          },
          transactionTypeCode: transactionType.code,
        },
      });
    } else if (props.type === 'donor') {
      donorDetailAction({
        values: {
          filters: {
            ...filters,
            [AF_TRANSACTION_PROVIDER_ORG_REF]: [param],
          },
          transactionTypeCode: transactionType.code,
        },
      });
    } else if (props.type === 'publisher') {
      publisherDetailAction({
        values: {
          filters: {
            ...filters,
            [AF_REPORTING_ORG_REF]: [param],
          },
          transactionTypeCode: transactionType.code,
        },
      });
    } else if (props.type === 'organisation') {
      organisationDetailAction({
        values: {
          filters: {
            ...filters,
            [AF_PARTICIPATING_ORG_REF]: [param],
          },
          transactionTypeCode: transactionType.code,
        },
      });
    }
  }

  useUpdateEffect(
    () => loadData(),
    [pageParam, selectedFilters, transactionType]
  );

  React.useEffect(() => {
    filtersUpdatedClear();
    if (
      props.type === 'country' &&
      !get(countryDetailData, 'data.activitiesCount', null)
    ) {
      loadData();
    }
    if (
      props.type === 'donor' &&
      !get(donorDetailData, 'data.activitiesCount', null)
    ) {
      loadData();
    }
    if (
      props.type === 'publisher' &&
      !get(publisherDetailData, 'data.activitiesCount', null)
    ) {
      loadData();
    }
    if (
      props.type === 'organisation' &&
      !get(organisationDetailData, 'data.activitiesCount', null)
    ) {
      loadData();
    }
    return () => filtersUpdatedClear();
  }, []);

  React.useEffect(() => {
    const updStatData: OverviewStatItemProps[] = [...statData];
    updStatData[0].value = get(
      detailData,
      'data.activitiesCount',
      0
    ).toLocaleString('nl-NL');
    updStatData[1].value = get(
      detailData,
      'data.donorsCount',
      0
    ).toLocaleString('nl-NL');
    updStatData[2].value = get(
      detailData,
      'data.publishersCount',
      0
    ).toLocaleString('nl-NL');
    updStatData[3].value = get(
      detailData,
      'data.sectorsCount',
      0
    ).toLocaleString('nl-NL');
    updStatData[4].value = get(
      detailData,
      'data.organisationsCount',
      0
    ).toLocaleString('nl-NL');

    updStatData[0].path = `${Path.basePath}${props.type}/${pageParam}/projects${location.search}`;
    updStatData[1].path = `${Path.basePath}${props.type}/${pageParam}/donors${location.search}`;
    updStatData[2].path = `${Path.basePath}${props.type}/${pageParam}/publishers${location.search}`;
    updStatData[3].path = `${Path.basePath}${props.type}/${pageParam}/sectors${location.search}`;
    updStatData[4].path = `${Path.basePath}${props.type}/${pageParam}/organisations${location.search}`;

    setStatData(updStatData);
  }, [detailData, props.type, location.search]);

  useUnmount(() => {
    countryDetailClearAction();
    donorDetailClearAction();
    publisherDetailClearAction();
    organisationDetailClearAction();
    treemapClearAction();
    sunburstClearAction();
    lineClearAction();
    sectorsTableClearAction();
    detailPublishersTableClearAction();
    detailDonorsTableClearAction();
    lineClearAction();
  });

  const loading = useStoreState(
    (state) =>
      state.countryDetail.loading ||
      state.donorDetail.loading ||
      state.publisherDetail.loading ||
      state.organisationDetail.loading ||
      state.linechart.loading ||
      state.treemap.loading ||
      state.sunburst.loading ||
      state.sectorsTable.loading ||
      state.detailDonorsTable.loading ||
      state.detailPublishersTable.loading ||
      state.detailActivities.loading ||
      state.organisationsTreemap.loading
  );

  return (
    <DetailModuleLayout
      type={props.type}
      param={param}
      loading={loading}
      statData={statData}
      lineData={lineData}
      detailBaseFilter={detailBaseFilter}
      pageTitle={getPageTitle(props.type, pageParam, detailData)}
      breadcrumbs={getBreadcrumbs(props.type, pageParam, detailData)}
      subText={
        props.type === 'country'
          ? get(countryDetailData, 'data.wikiExcerpt', '')
          : ''
      }
      wikiLink={
        props.type === 'country'
          ? get(countryDetailData, 'data.wikiLink', null)
          : null
      }
    />
  );
}
